import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Zap, DollarSign, MessageSquare, TrendingUp } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, type AdminUser } from "../../../api";
import { Eyebrow } from "../../../components/ui";
import KpiCard from "../../../components/charts/KpiCard";
import { fadeUp, stagger } from "../../../lib/motion";
import { fmtUsd, fmtNum } from "../../../lib/format";

export default function Overview() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // Fetch up to ~3 pages to get a sample for client-side KPIs
        const all: AdminUser[] = [];
        let cursor: string | undefined;
        for (let i = 0; i < 3; i++) {
          const res = await api.listUsers(cursor);
          all.push(...res.users);
          if (!res.next_cursor) break;
          cursor = res.next_cursor;
        }
        setUsers(all);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "load failed");
      }
    })();
  }, []);

  if (err) return <div className="text-terracotta">{err}</div>;
  if (!users) return <div className="text-ink-faint">Loading overview…</div>;

  const now = Date.now() / 1000;
  const day = 86400;
  const active7d = users.filter((u) => u.last_bot_msg_at && now - u.last_bot_msg_at < 7 * day).length;
  const active30d = users.filter((u) => u.last_bot_msg_at && now - u.last_bot_msg_at < 30 * day).length;
  const new7d = users.filter((u) => u.activated_at && now - u.activated_at < 7 * day).length;
  const totalMsgs = users.reduce((s, u) => s + (u.lifetime_msgs_out ?? 0), 0);
  const totalCost = users.reduce((s, u) => s + (u.lifetime_cost_usd ?? 0), 0);
  const avgCost = users.length ? totalCost / users.length : 0;

  // DAU proxy: bucket users by their last_bot_msg day for last 30 days
  const dauBuckets: Record<string, number> = {};
  for (let d = 29; d >= 0; d--) {
    const iso = new Date(Date.now() - d * day * 1000).toISOString().slice(0, 10);
    dauBuckets[iso] = 0;
  }
  for (const u of users) {
    if (!u.last_bot_msg_at) continue;
    const iso = new Date(u.last_bot_msg_at * 1000).toISOString().slice(0, 10);
    if (iso in dauBuckets) dauBuckets[iso]++;
  }
  const dauData = Object.entries(dauBuckets).map(([date, v]) => ({ date: date.slice(5), active: v }));

  // Signups funnel
  const signedUp = users.length;
  const verified = users.filter((u) => u.email).length; // proxy
  const onboarded = users.filter((u) => u.first_name).length;
  const linked = users.filter((u) => u.telegram_user_id).length;
  const firstMsg = users.filter((u) => u.lifetime_msgs_out > 0).length;
  const funnel = [
    { stage: "Signed up", v: signedUp },
    { stage: "Verified", v: verified },
    { stage: "Onboarded", v: onboarded },
    { stage: "Linked TG", v: linked },
    { stage: "First message", v: firstMsg },
  ];

  // Top users by cost
  const topCost = [...users]
    .filter((u) => u.lifetime_cost_usd > 0)
    .sort((a, b) => b.lifetime_cost_usd - a.lifetime_cost_usd)
    .slice(0, 10);

  // Cost by language
  const byLang: Record<string, number> = {};
  for (const u of users) {
    const k = u.active_language || "?";
    byLang[k] = (byLang[k] || 0) + (u.lifetime_cost_usd ?? 0);
  }
  const langData = Object.entries(byLang).map(([lang, cost]) => ({ lang: lang.toUpperCase(), cost }));

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.08)}>
      <motion.div variants={fadeUp}>
        <div className="font-serif italic text-ink-soft">admin · overview</div>
        <h2 className="display text-4xl md:text-5xl mt-2">System pulse.</h2>
      </motion.div>

      {/* KPI strip */}
      <motion.div variants={fadeUp} className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label="total users" value={fmtNum(users.length)} icon={<Users size={14} />} />
        <KpiCard label="active 7d" value={fmtNum(active7d)} delta={`${((active7d / Math.max(users.length, 1)) * 100).toFixed(0)}% of total`} icon={<Zap size={14} />} />
        <KpiCard label="active 30d" value={fmtNum(active30d)} icon={<TrendingUp size={14} />} />
        <KpiCard label="new 7d" value={fmtNum(new7d)} icon={<Users size={14} />} />
        <KpiCard label="lifetime msgs" value={fmtNum(totalMsgs)} icon={<MessageSquare size={14} />} />
        <KpiCard label="lifetime cost" value={fmtUsd(totalCost)} delta={`${fmtUsd(avgCost, 4)} / user`} icon={<DollarSign size={14} />} tone="ink" />
      </motion.div>

      {/* DAU chart */}
      <motion.div variants={fadeUp} className="mt-6 card">
        <div className="mb-4">
          <Eyebrow>daily active users · last 30 days</Eyebrow>
          <h3 className="font-serif text-2xl mt-1">Engagement trend</h3>
        </div>
        <div className="h-56">
          <ResponsiveContainer>
            <AreaChart data={dauData}>
              <defs>
                <linearGradient id="dau" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B8472C" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#B8472C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(42,36,26,0.08)" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A8079" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8A8079" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#15181F", border: "none", borderRadius: 6, color: "#F4ECDD", fontSize: 12 }} />
              <Area type="monotone" dataKey="active" stroke="#B8472C" strokeWidth={2} fill="url(#dau)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Funnel + cost by lang */}
      <motion.div variants={fadeUp} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="card">
          <div className="mb-4">
            <Eyebrow>activation funnel</Eyebrow>
            <h3 className="font-serif text-2xl mt-1">From signup to first hallo</h3>
          </div>
          <div className="space-y-3">
            {funnel.map((f, i) => {
              const pct = (f.v / Math.max(signedUp, 1)) * 100;
              const prev = i > 0 ? funnel[i - 1].v : f.v;
              const stageConv = prev > 0 ? ((f.v / prev) * 100).toFixed(0) : "—";
              return (
                <div key={f.stage}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-ink-soft">{f.stage}</span>
                    <span className="font-mono">
                      {f.v}{" "}
                      <span className="text-ink-faint">· {stageConv}%</span>
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-[rgba(42,36,26,0.08)] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.9, delay: i * 0.1 }}
                      className="h-full bg-terracotta"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="mb-4">
            <Eyebrow>cost by language</Eyebrow>
            <h3 className="font-serif text-2xl mt-1">Where compute goes</h3>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={langData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(42,36,26,0.08)" vertical={false} />
                <XAxis dataKey="lang" tick={{ fontSize: 11, fill: "#8A8079" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8A8079" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip contentStyle={{ background: "#15181F", border: "none", borderRadius: 6, color: "#F4ECDD", fontSize: 12 }} formatter={(v: number) => fmtUsd(v, 4)} />
                <Bar dataKey="cost" fill="#B8472C" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      {/* Top users */}
      <motion.div variants={fadeUp} className="mt-6 card">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Eyebrow>top 10 users · by lifetime cost</Eyebrow>
            <h3 className="font-serif text-2xl mt-1">Your power users</h3>
          </div>
        </div>
        {topCost.length === 0 && <p className="text-ink-faint text-sm">No usage data yet.</p>}
        <div className="space-y-2">
          {topCost.map((u, i) => {
            const max = topCost[0]?.lifetime_cost_usd ?? 1;
            const pct = (u.lifetime_cost_usd / max) * 100;
            return (
              <div key={u.sub} className="flex items-center gap-3">
                <div className="w-6 text-xs font-mono text-ink-faint text-right">{i + 1}</div>
                <div className="w-32 truncate text-sm">{u.first_name || u.email.split("@")[0]}</div>
                <div className="flex-1 h-2 bg-[rgba(42,36,26,0.08)] rounded-full overflow-hidden">
                  <div className="h-full bg-terracotta" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-24 text-right text-sm font-mono">{fmtUsd(u.lifetime_cost_usd, 4)}</div>
                <div className="w-20 text-right text-xs text-ink-faint">{fmtNum(u.lifetime_msgs_out)} msgs</div>
              </div>
            );
          })}
        </div>
      </motion.div>

    </motion.div>
  );
}
